import React, { useEffect, useState, useCallback } from "react";
import {
  Row,
  Col,
  Table,
  Button,
  Dropdown,
  Menu,
  Modal,
  Form,
  Input,
  message,
  Breadcrumb,
} from "antd";
import {
  EllipsisOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import theme from "../theme";
import { listFunctions, createFunction, updateFunction, deleteFunction } from "../services/functions";
import { getIndustry } from "../services/industries";

interface FunctionRow {
  id: string;
  name: string;
  created_at: string;
}

interface Industry {
  id: number;
  name: string;
  color_hex: string;
}

const IndustryFunctions: React.FC = () => {
  const { industryId } = useParams();
  const navigate = useNavigate();

  const [industry, setIndustry] = useState<Industry | null>(null);
  const [rows, setRows] = useState<FunctionRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingFunction, setEditingFunction] = useState<FunctionRow | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState<FunctionRow | null>(null);
  const [form] = Form.useForm();

  const fetchData = useCallback(async () => {
    if (!industryId) return;
    setLoading(true);
    try {
      const [industryData, functionsData] = await Promise.all([
        getIndustry(Number(industryId)),
        listFunctions(Number(industryId)),
      ]);
      setIndustry(industryData);
      setRows(
        functionsData.map((f: any) => ({
          id: String(f.id),
          name: f.name,
          created_at: f.created_at,
        }))
      );
    } catch (error) {
      message.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  }, [industryId]);

  useEffect(() => {
    if (!industryId) return;
    fetchData();
  }, [industryId, fetchData]);

  const columns = [
    { title: "Function Name", dataIndex: "name", key: "name" },
    {
      title: "Created At",
      dataIndex: "created_at",
      key: "created_at",
      render: (date: string) => new Date(date).toLocaleDateString(),
    },
    {
      key: "actions",
      width: 60,
      render: (_: unknown, row: FunctionRow) => (
        <Dropdown
          overlay={
            <Menu>
              <Menu.Item onClick={() => handleEdit(row)}>
                Edit
              </Menu.Item>
              <Menu.Item danger onClick={() => setToDelete(row)}>
                Delete
              </Menu.Item>
            </Menu>
          }
        >
          <Button type="text" icon={<EllipsisOutlined />} />
        </Dropdown>
      ),
    },
  ];

  const handleAdd = () => {
    setEditingFunction(null);
    form.resetFields();
    setModalOpen(true);
  };

  const handleEdit = (functionRow: FunctionRow) => {
    setEditingFunction(functionRow);
    form.setFieldsValue({ name: functionRow.name });
    setModalOpen(true);
  };

  const handleSave = async (values: { name: string }) => {
    if (!industryId) return;

    setSaving(true);
    try {
      if (editingFunction) {
        await updateFunction(Number(editingFunction.id), { name: values.name });
        message.success("Function updated successfully");
      } else {
        await createFunction({
          industry: Number(industryId),
          name: values.name,
        });
        message.success("Function created successfully");
      }
      setModalOpen(false);
      fetchData();
    } catch (error) {
      message.error("Failed to save function");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!toDelete) return;

    setDeleting(true);
    try {
      await deleteFunction(Number(toDelete.id));
      message.success("Function deleted successfully");
      setToDelete(null);
      fetchData();
    } catch (error) {
      message.error("Failed to delete function");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div style={bg}>
      <Breadcrumb style={{ marginBottom: 16 }}>
        <Breadcrumb.Item>
          <Button
            type="link"
            onClick={() => navigate("/industries-list")}
            style={{ padding: 0, height: 'auto' }}
          >
            Industries
          </Button>
        </Breadcrumb.Item>
        <Breadcrumb.Item>
          <Button
            type="link"
            onClick={() => navigate(`/view-industry/${industryId}`)}
            style={{ padding: 0, height: 'auto' }}
          >
            {industry?.name || "Loading..."}
          </Button>
        </Breadcrumb.Item>
      </Breadcrumb>

      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Row align="middle" gutter={12}>
          <Col>
            <h1 style={heading}>Functions</h1>
          </Col>
        </Row>

        <Col>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            style={createBtn}
            onClick={handleAdd}
          >
            Add new
          </Button>
        </Col>
      </Row>

      <div style={card}>
        <Table
          columns={columns}
          dataSource={rows}
          rowKey="id"
          loading={loading}
          pagination={false}
          bordered
          locale={{
            emptyText: (
              <div style={{ padding: '40px 0', textAlign: 'center', color: '#666' }}>
                <div style={{ fontSize: '16px', marginBottom: '8px' }}>
                  No functions found
                </div>
                <div style={{ fontSize: '14px' }}>
                  Create your first function to get started
                </div>
              </div>
            )
          }}
        />
      </div>

      {/* Add/Edit Modal */}
      <Modal
        open={modalOpen}
        title={editingFunction ? "Edit Function" : "Add New Function"}
        okText="Save"
        okButtonProps={{ loading: saving }}
        cancelButtonProps={{ disabled: saving }}
        onOk={() => form.submit()}
        onCancel={() => setModalOpen(false)}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSave}
        >
          <Form.Item
            name="name"
            label="Function Name"
            rules={[{ required: true, message: "Please enter function name" }]}
          >
            <Input placeholder="Enter function name" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Delete Modal */}
      <Modal
        open={!!toDelete}
        title="Delete Function?"
        okText="Delete"
        okButtonProps={{ danger: true, loading: deleting }}
        cancelButtonProps={{ disabled: deleting }}
        onOk={handleDelete}
        onCancel={() => setToDelete(null)}
      >
        Are you sure you want to delete "{toDelete?.name}"?
      </Modal>
    </div>
  );
};

const bg: React.CSSProperties = {
  minHeight: "100vh",
};

const heading: React.CSSProperties = {
  fontSize: 28,
  fontWeight: 700,
  color: theme.colors.primary,
  margin: 0,
};



const card: React.CSSProperties = {
  background: "#ffffff",
  borderRadius: 8
};

const createBtn: React.CSSProperties = {
  background: theme.colors.primary,
  borderRadius: 9999,
  border: "none",
  padding: "0 24px",
  fontWeight: 600,
};

export default IndustryFunctions;